import jsPDF from 'jspdf';
import { format } from 'date-fns';

// PHA-1104: the single-patient billing report PDF — billing summary and the time & activity
// audit trail combined into ONE document, which is the whole point of the ticket: a biller
// filing the CSVs into a chart would be filing two half-documents.
//
// Rendered client-side with jsPDF, like every other PDF in this app (there is no
// server-side renderer). The same bytes are what get pushed to the EHR, so what a biller
// sees here is exactly what lands in the chart.

/** One month's summary row, as the Billing Report page already holds it. */
export interface BillingReportPdfSummary {
  month: string;                    // 'YYYY-MM'
  patientId: string;                // MRN
  patientName: string;
  dateOfBirth?: string | null;
  icd10Codes?: string | null;
  program?: string | null;
  carePlanCreated?: string | null;
  consentDate?: string | null;
  cptCodes?: string | null;
  rpmCodes?: string | null;
  /**
   * The month's billable minutes — the SAME figure the on-screen Billing Summary and the
   * Export Summary CSV render. Deliberately not re-derived here: a document that attests to
   * a billed code must not show a different number from the report it was generated off.
   */
  spentMinutes: number;
  /**
   * The backend-reconciled total for the AUDIT TRAIL below (per-(patient, month)
   * round-then-sum). Optional; when absent the footer falls back to summing the rows.
   *
   * Separate from `spentMinutes` because the two genuinely differ on a partial-month range:
   * the audit rows are begin_ts-filtered to the range, while the summary row covers the
   * whole billed month. Collapsing them would make one of the two numbers wrong.
   */
  loggedMinutes?: number | null;
  patientActivities: number;
}

/**
 * One line of the audit trail. Care-team work and patient-side events are ONE row type on
 * purpose — an auditor reading the trail wants them interleaved in time, not in two lists,
 * and `source` is what tells them apart. Same shape the Export Details CSV emits.
 */
export interface BillingReportPdfActivity {
  month: string;                    // 'YYYY-MM' — which summary section this belongs under
  timestamp: string;                // ISO; ordering and the Date/Time columns
  action: string;
  performedBy: string;
  durationMinutes: number | null;   // null for point-in-time patient events, never 0
  source: 'Care Team' | 'Patient';
  detail: string;
}

export interface BillingReportPdfOptions {
  summaries: BillingReportPdfSummary[];
  activities: BillingReportPdfActivity[];
  /** Inclusive reporting range, YYYY-MM-DD — the biller's on-screen filter. */
  fromDate: string;
  toDate: string;
  /** Phanova's own mark, not the clinic's — see the branding note below. */
  logoUrl?: string;
}

// Normalize smart punctuation. Without this an em dash or a curly apostrophe in a note
// renders as mojibake in a document that goes into a medical record.
const normalizePunctuation = (text: string): string =>
  (text ?? '')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-');

/** Single-line surfaces: headings, the title, the footer. Every control character goes. */
const sanitize = (text: string): string =>
  normalizePunctuation(text).replace(/[^\x20-\x7E]/g, '');

/**
 * Table-cell variant, which keeps real line breaks.
 *
 * PHA-891 merges an island's contributing actions into ONE Action cell: the event that
 * determines the billed time on the first line, any other contributing actions on a second
 * (the same two-line shape `BillingReport.tsx` renders on screen). `sanitize` dropped that
 * newline along with every other control character, so the two names ran together as
 * "Smart Hub AcknowledgementActive Care Management Time" in a document that is filed into a
 * medical record and read by an auditor.
 *
 * Keeping the newline is enough: jsPDF's splitTextToSize breaks on it, and drawRow already
 * sizes the row from the wrapped line count, so the cell grows to fit.
 */
const sanitizeCell = (text: string): string =>
  normalizePunctuation(text)
    .replace(/\r\n?/g, '\n')
    .replace(/[^\x20-\x7E\n]/g, '');

/** 'YYYY-MM' -> 'MMM yyyy' without touching the timezone (day 15 is never near a boundary). */
const formatMonthLabel = (month: string): string => {
  const [year, mon] = String(month || '').split('-');
  const d = new Date(Number(year), Number(mon) - 1, 15);
  return Number.isNaN(d.getTime()) ? month : format(d, 'MMM yyyy');
};

/**
 * Distinct, non-empty values in the order they first appear, joined with `separator`.
 *
 * PHA-1129 draws the patient block once for the whole report, but two of its cells are
 * really per-MONTH facts: an APCM tier can change between months (PHA-1108, PHA-618) and a
 * month's diagnosis set can grow. Showing only the first month's value would silently drop
 * the others from a document that supports a Medicare claim, so keep every distinct one.
 */
const distinctJoined = (values: (string | null | undefined)[], separator: string): string =>
  Array.from(new Set(values.map((v) => String(v ?? '').trim()).filter(Boolean))).join(separator);

/**
 * Distinct ICD-10 CODES, not distinct month-strings: 'E78.5, I10' and 'E78.5, E11.9' are
 * three codes between them, and joining the two strings whole would repeat E78.5.
 */
const distinctIcd10 = (values: (string | null | undefined)[]): string =>
  distinctJoined(values.flatMap((v) => String(v ?? '').split(',')), ', ');

/** A cell's value reduced to what "the same value" means for it — for comparison only. */
const identityKey = (value: string | null | undefined): string =>
  String(value ?? '').trim();

/**
 * ICD-10 compared as a SET of codes. 'E78.5, I10' and 'I10, E78.5' are the same diagnosis
 * list written two ways; treating the reordering as a change would push the codes into the
 * monthly sections for a patient whose diagnoses never moved.
 */
const icd10Key = (value: string | null | undefined): string =>
  Array.from(new Set(String(value ?? '').split(',').map((c) => c.trim()).filter(Boolean)))
    .sort()
    .join(',');

/** True when `read` gives more than one distinct value across the months. */
const varies = <T,>(rows: T[], read: (row: T) => string): boolean =>
  rows.length > 1 && new Set(rows.map(read)).size > 1;

/**
 * The document's period identity. MUST agree with app_logic._billing_period_label — that is
 * what the server dedups on and names the filed document with, so a divergence here would
 * mean the PDF says one period and the chart says another.
 */
export const billingPeriodLabel = (fromDate: string, toDate: string): string => {
  const start = String(fromDate || '').slice(0, 7);
  const end = String(toDate || '').slice(0, 7);
  return start === end ? start : `${start}..${end}`;
};

/** "PHANOVA monthly report <MRN> <period>.pdf" — the name PHA-1104 specifies. */
export const billingReportPdfFilename = (patientId: string, fromDate: string, toDate: string): string =>
  `PHANOVA monthly report ${patientId} ${billingPeriodLabel(fromDate, toDate).replace('..', '_to_')}.pdf`;

/** Loads an image URL into a data URL for embedding; null on any failure. */
async function loadLogoDataUrl(url?: string): Promise<string | null> {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  try {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const MARGIN = 16;

/**
 * Vertical rhythm. Every heading owns the space ABOVE it and sits tight against what it
 * labels — a heading belongs to the table under it, not to the one it follows.
 *
 * PHA-1129 (Addi's review): the space used to sit under each heading instead, which read
 * backwards. "Billed Codes - Summary" floated between the rule above it and its own table,
 * and the patient block gave the month heading that follows it barely a third of the gap a
 * month heading gets between months, so "Apr 2026" sat on the patient table's bottom border
 * while "May 2026" had air above it. One constant per kind of heading, applied everywhere,
 * so the two can no longer drift apart.
 */
const MONTH_TOP_PAD = 12;       // above a month heading — the report's largest break
const SECTION_TOP_PAD = 8;      // above "Billed Codes" / "Time & Activity"
const TABLE_TOP_PAD = 5;        // above a table that follows a rule with no heading
const HEADING_BOTTOM_PAD = 1.5; // just enough to clear the descenders

/**
 * The "Total logged activity time this period" line: the air above it (PHA-1113 #1 — it
 * used to sit on the table's bottom border and read as another row) plus the line itself.
 * ONE constant, used both to reserve the band and to keep it on the same page as the last
 * row of the table it is about: a billing figure alone at the top of a page, with no table
 * behind it, is the thing this is guarding against.
 */
const TOTAL_TOP_PAD = 7;
const TOTAL_BAND = TOTAL_TOP_PAD + 8;

const HEADING_COLOR: [number, number, number] = [31, 58, 95];
const TABLE_HEADER_FILL: [number, number, number] = [31, 58, 95];
const ROW_STRIPE: [number, number, number] = [244, 246, 249];
const BORDER: [number, number, number] = [200, 206, 214];

/**
 * Builds the combined billing report and returns it as a Blob.
 *
 * Layout (PHA-1129): title, then the patient block once, then one section per month --
 * month heading, billed codes, audit log, that month's total.
 *
 * The per-month sections are NOT merged into one table: the billed codes and the total
 * time are per-month facts — that is the unit Medicare is billed in — and flattening them
 * would produce a number that is not billable for any period. For the same reason there is
 * no cross-month grand total under the last section.
 */
export async function generateBillingReportPdf(opts: BillingReportPdfOptions): Promise<Blob> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - 2 * MARGIN;
  // Reserve the footer band so no table row is drawn over the disclaimer.
  const bottomLimit = pageHeight - 24;
  let yPos = 18;

  const ensureRoom = (needed: number) => {
    if (yPos + needed > bottomLimit) {
      doc.addPage();
      yPos = 18;
    }
  };

  // ---- Branding ----
  // Phanova's mark, deliberately NOT the clinic logo the care-plan PDF uses. This document
  // is Phanova attesting to the time it recorded on the clinic's behalf; putting the
  // clinic's own logo on it would present our audit trail as theirs. The ticket's filename
  // ("PHANOVA monthly report ...") and PHA-1105's "Phanova Reports Destination Folder" both
  // read the same way.
  const logoDataUrl = await loadLogoDataUrl(opts.logoUrl);
  const brandText = 'Phanova Continuous Care';
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  const brandWidth = doc.getTextWidth(brandText);
  if (logoDataUrl) {
    try {
      const maxH = 8;
      const props = doc.getImageProperties(logoDataUrl);
      if (!props.width || !props.height) throw new Error('invalid image dimensions');
      const drawH = maxH;
      const drawW = (props.width / props.height) * drawH;
      const totalW = drawW + 4 + brandWidth;
      const startX = (pageWidth - totalW) / 2;
      const fmt = (logoDataUrl.match(/^data:image\/([^;]+)/)?.[1] ?? '').toUpperCase();
      doc.addImage(logoDataUrl, fmt, startX, yPos - drawH + 1.5, drawW, drawH);
      doc.setTextColor(...HEADING_COLOR);
      doc.text(brandText, startX + drawW + 4, yPos);
    } catch {
      doc.setTextColor(...HEADING_COLOR);
      doc.text(brandText, pageWidth / 2, yPos, { align: 'center' });
    }
  } else {
    doc.setTextColor(...HEADING_COLOR);
    doc.text(brandText, pageWidth / 2, yPos, { align: 'center' });
  }
  yPos += 8;

  const periodLabel = billingPeriodLabel(opts.fromDate, opts.toDate);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(90, 90, 90);
  doc.text(
    sanitize(`Monthly Care Management Billing Report & Audit Trail - ${periodLabel}`),
    pageWidth / 2, yPos, { align: 'center' },
  );
  yPos += 5;

  doc.setDrawColor(...HEADING_COLOR);
  doc.setLineWidth(0.8);
  doc.line(MARGIN, yPos, pageWidth - MARGIN, yPos);
  doc.setTextColor(0, 0, 0);
  yPos += 8;

  // ---- Section heading ----
  /**
   * `keepWith` is the height of the table that follows the heading, enough of it that the
   * heading never ends a page alone. Without it a section could break between its title and
   * its column headers, or leave a bare header row with no data under it at the foot of a
   * page — which is what the multi-month report did between its July heading and its rows.
   */
  const sectionHeading = (text: string, keepWith = 0) => {
    // The pad goes on BEFORE the room check, so a heading pushed to a new page loses it
    // rather than opening that page with a band of white space.
    yPos += SECTION_TOP_PAD;
    ensureRoom(10 + keepWith);
    doc.setFontSize(11.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...HEADING_COLOR);
    doc.text(sanitize(text), MARGIN, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += HEADING_BOTTOM_PAD;
  };

  /**
   * The heading that opens one month's set of tables. Deliberately heavier than
   * `sectionHeading` and followed by a rule: PHA-1129 moved the patient block out from
   * between it and "Billed Codes - Summary", and at the same size and weight the two ran
   * together as a single two-line label. The rule is what says "a new set of tables starts
   * here" — the whole job of this heading in a multi-month report.
   */
  const monthHeading = (text: string, keepWith = 0) => {
    yPos += MONTH_TOP_PAD;
    ensureRoom(16 + keepWith);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...HEADING_COLOR);
    doc.text(sanitize(text), MARGIN, yPos);
    yPos += 2.5;
    doc.setDrawColor(...HEADING_COLOR);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, yPos, pageWidth - MARGIN, yPos);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    // Whatever comes next brings its own top pad; the rule only needs to clear its width.
    yPos += 0.5;
  };

  /**
   * Draws one table row and advances yPos, breaking to a new page when it will not fit.
   * `repeatHeader` is re-drawn at the top of every continuation page — an audit trail whose
   * columns are only labelled on page 1 is unreadable on page 3.
   */
  const drawRow = (
    cells: string[],
    widths: number[],
    kind: 'header' | 'body' | 'label',
    stripe = false,
    repeatHeader?: { cells: string[]; widths: number[] },
    /**
     * Height of what MUST stay on the same page as this row. Used for the last row of an
     * audit trail, whose "Total logged activity time" line is a statement about the table:
     * on its own at the top of the next page it reads as a figure with nothing behind it.
     */
    keepWith = 0,
  ) => {
    const pad = 1.8;
    const lineHeight = 4.2;
    doc.setFontSize(8);
    doc.setFont('helvetica', kind === 'body' ? 'normal' : 'bold');

    const wrapped = cells.map((c, i) =>
      doc.splitTextToSize(sanitizeCell(c || ''), widths[i] - 2 * pad),
    );
    const rowHeight = Math.max(lineHeight, ...wrapped.map((l) => l.length * lineHeight));
    const boxHeight = rowHeight + pad * 2;

    if (yPos + boxHeight + keepWith > bottomLimit) {
      doc.addPage();
      yPos = 18;
      if (repeatHeader) {
        drawRow(repeatHeader.cells, repeatHeader.widths, 'header');
        // PHA-1113 #4: that nested call left the font BOLD (it drew a header). Without
        // restoring it here, the row that happened to trigger the page break renders bold
        // — which is why the first activity row of every continuation page was emboldened.
        // Set, don't move: the pair above must stay above splitTextToSize, which measures
        // against whatever font is current.
        doc.setFontSize(8);
        doc.setFont('helvetica', kind === 'body' ? 'normal' : 'bold');
      }
    }

    if (kind === 'header') {
      doc.setFillColor(...TABLE_HEADER_FILL);
      doc.rect(MARGIN, yPos, widths.reduce((a, b) => a + b, 0), boxHeight, 'F');
      doc.setTextColor(255, 255, 255);
    } else {
      if (stripe || kind === 'label') {
        doc.setFillColor(...(kind === 'label' ? ROW_STRIPE : ROW_STRIPE));
        doc.rect(MARGIN, yPos, widths.reduce((a, b) => a + b, 0), boxHeight, 'F');
      }
      doc.setTextColor(30, 30, 30);
    }

    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    let cx = MARGIN;
    widths.forEach((w) => {
      doc.rect(cx, yPos, w, boxHeight, 'S');
      cx += w;
    });

    cx = MARGIN;
    wrapped.forEach((lines, i) => {
      let ty = yPos + pad + lineHeight * 0.78;
      lines.forEach((line: string) => {
        doc.text(line, cx + pad, ty);
        ty += lineHeight;
      });
      cx += widths[i];
    });

    doc.setTextColor(0, 0, 0);
    yPos += boxHeight;
  };

  // ---- Month order ----
  // The backend hands the summary rows back NEWEST-first. The document reads as a range
  // ("2026-03..2026-05"), and the audit rows inside each month are already chronological,
  // so counting the months down while their rows count up reads as a mistake. Sorted here
  // rather than at the call site so Download and Push to EHR cannot diverge.
  // 'YYYY-MM' is lexicographically ordered, so a string compare IS the chronological one.
  const summaries = [...opts.summaries].sort((a, b) =>
    String(a.month ?? '').localeCompare(String(b.month ?? '')),
  );

  // ---- Patient identity, stated ONCE ----
  // PHA-1129: this block used to be redrawn at the top of every month's section, so a
  // three-month report introduced the same patient three times. Name, MRN and date of
  // birth are facts about the PATIENT, not about a billing month; the only cell that ever
  // varied was "Billing Period", which now names the whole range the biller selected --
  // the same label the title, the footer, the filename and the server's dedup key use.
  // Which month each set of tables below belongs to is said by its own heading instead.
  //
  // Addi's review: Program and ICD-10 Codes are the two cells here that can legitimately
  // differ between months — an APCM tier is recalculated every month (PHA-1108, PHA-618)
  // and a patient's diagnosis list can grow. When they DO differ the value belongs to the
  // month, not to the patient, so it is stated inside that month's own section instead of
  // as a joined set up here. When nothing changed — the normal case, and every
  // single-month report — they stay in this block and the document reads as it does today.
  const months = summaries.map((s) => s.month);
  const programPerMonth = varies(summaries, (s) => identityKey(s.program));
  const icd10PerMonth = varies(summaries, (s) => icd10Key(s.icd10Codes));

  const idWidths = [contentWidth * 0.18, contentWidth * 0.32, contentWidth * 0.18, contentWidth * 0.32];
  const wideIdWidths = [contentWidth * 0.18, contentWidth * 0.82];

  /**
   * Label/value pairs, two to a row. A trailing ODD pair takes the full width rather than
   * leaving two empty boxes at the end of the grid — which is exactly the shape this block
   * takes when one of Program / ICD-10 moves into the monthly sections and the other stays.
   */
  const drawPairs = (pairs: [string, string][]) => {
    for (let i = 0; i < pairs.length; i += 2) {
      const stripe = (i / 2) % 2 === 0;
      const [label, value] = pairs[i];
      const second = pairs[i + 1];
      if (second) drawRow([label, value, second[0], second[1]], idWidths, 'body', stripe);
      else drawRow([label, value], wideIdWidths, 'body', stripe);
    }
  };

  const first = summaries[0];
  if (first) {
    const pairs: [string, string][] = [
      ['Patient Name', first.patientName],
      ['MRN', first.patientId],
      ['Date of Birth', first.dateOfBirth || '-'],
      ['Billing Period', periodLabel],
    ];
    if (!icd10PerMonth) {
      pairs.push(['ICD-10 Codes', distinctIcd10(summaries.map((s) => s.icd10Codes)) || '-']);
    }
    if (!programPerMonth) {
      pairs.push(['Program', distinctJoined(summaries.map((s) => s.program), '; ') || 'CCM-Only']);
    }
    pairs.push(['Care Plan Created', first.carePlanCreated || '-']);
    pairs.push(['Consent Date', first.consentDate || '-']);
    drawPairs(pairs);
  }

  // ---- One section per month ----
  summaries.forEach((summary) => {
    // A multi-month report needs to say which month each set of tables is. On a
    // single-month report the period is already stated three times over (title, the
    // "Billing Period" cell above, footer), so a heading there would only repeat it.
    if (months.length > 1) {
      // Keep it with the billed-codes heading and that table's two rows, so a month never
      // opens at the foot of a page with nothing under it.
      monthHeading(formatMonthLabel(summary.month), 30);
    }

    // The identity cells that turned out to be per-month facts, restated for THIS month.
    // Same label/value shape as the patient block above, directly under the month's rule,
    // so it reads as "what was true for this month" before the codes billed for it.
    if (icd10PerMonth || programPerMonth) {
      yPos += TABLE_TOP_PAD;
      const monthPairs: [string, string][] = [];
      if (icd10PerMonth) monthPairs.push(['ICD-10 Codes', summary.icd10Codes || '-']);
      if (programPerMonth) monthPairs.push(['Program', summary.program || 'CCM-Only']);
      drawPairs(monthPairs);
    }

    // Billed codes. Only the four columns the mockup carries — the RPM data-day count and
    // service dates stay on screen and in the CSV.
    // Header row plus the single data row that follows it.
    sectionHeading('Billed Codes - Summary', 18);
    const codeWidths = [contentWidth * 0.28, contentWidth * 0.22, contentWidth * 0.25, contentWidth * 0.25];
    const codeHeaders = ['Care Mgmt CPT Code(s)', 'RPM Code(s)', 'Total Time (min)', 'Patient Activities'];
    drawRow(codeHeaders, codeWidths, 'header');
    drawRow(
      [
        summary.cptCodes || '-',
        summary.rpmCodes || '-',
        // One decimal, matching the Billing Summary table on screen. The two numbers being
        // read side by side is the normal case, and a rounding difference between them
        // reads as a discrepancy in the thing this document exists to attest to.
        summary.spentMinutes.toFixed(1),
        String(summary.patientActivities),
      ],
      codeWidths, 'body',
    );

    // Audit trail for this month.
    // Header row (its 'Duration (min)' cell wraps to two lines) plus one activity row.
    sectionHeading('Time & Activity Detail Log (Audit Trail)', 26);
    const logWidths = [
      contentWidth * 0.11, contentWidth * 0.09, contentWidth * 0.18, contentWidth * 0.18,
      contentWidth * 0.11, contentWidth * 0.10, contentWidth * 0.23,
    ];
    const logHeaders = ['Date', 'Time', 'Action', 'Performed By', 'Duration (min)', 'Source', 'Detail'];
    drawRow(logHeaders, logWidths, 'header');

    const monthRows = opts.activities
      .filter((a) => a.month === summary.month)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    if (monthRows.length === 0) {
      // Said outright rather than left as an empty grid: "no rows" and "the rows failed to
      // load" look identical otherwise, and this document is evidence.
      drawRow(
        ['No logged activity for this period.', '', '', '', '', '', ''],
        [contentWidth, 0, 0, 0, 0, 0, 0], 'body',
      );
    } else {
      monthRows.forEach((row, i) => {
        const ts = new Date(row.timestamp);
        const valid = !Number.isNaN(ts.getTime());
        drawRow(
          [
            valid ? format(ts, 'yyyy-MM-dd') : '',
            valid ? format(ts, 'h:mm a') : '',
            row.action,
            row.performedBy,
            // Blank, never '0': a patient's survey response is a point-in-time event, and
            // "0 minutes" would read as care that was delivered and took no time.
            row.durationMinutes === null ? '' : row.durationMinutes.toFixed(1),
            row.source,
            row.detail,
          ],
          logWidths, 'body', i % 2 === 1,
          { cells: logHeaders, widths: logWidths },
          // The last row carries the total band with it, or nothing does.
          i === monthRows.length - 1 ? TOTAL_BAND : 0,
        );
      });

      // The backend's reconciled figure when we have it. Summing the visible rows is a
      // fallback only: those are already rounded per row, and PHA-891 is the ticket about
      // exactly that sum disagreeing with the Billing Summary.
      const totalMinutes = typeof summary.loggedMinutes === 'number'
        ? summary.loggedMinutes
        : monthRows.reduce((sum, r) => sum + (r.durationMinutes ?? 0), 0);
      // PHA-1113 #1: the total sat almost on the table's bottom border and read as another
      // row of it. It is a statement ABOUT the table, so it needs air on both sides.
      yPos += TOTAL_TOP_PAD;
      // Normally a no-op — the last row reserved TOTAL_BAND. Kept for the one path that
      // did not go through drawRow: a month whose rows all fitted but whose table ends
      // flush against the bottom limit.
      ensureRoom(TOTAL_BAND - TOTAL_TOP_PAD);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(
        sanitize(
          `Total logged activity time this period: ${totalMinutes.toFixed(1)} minutes across ${monthRows.length} activities`,
        ),
        pageWidth - MARGIN, yPos, { align: 'right' },
      );
      doc.setTextColor(0, 0, 0);
      // Clear the descenders only. The gap to the NEXT month's heading is that heading's
      // MONTH_TOP_PAD, so it is the same gap the patient block gives the first one.
      yPos += 1.5;
    }
  });

  // ---- Footer ----
  // PHA-1113 #2/#3. Two different things used to be conflated into one repeated block:
  //
  //   * The document's IDENTITY — what report this is, whose chart it belongs in, what
  //     period it covers, and where in the document you are. That belongs on every page:
  //     a page of an audit trail that got detached from page 1 must still identify itself,
  //     and a loose page with no MRN on it cannot be filed.
  //   * The DISCLAIMER — a statement about the report as a whole. Repeating it on every
  //     page made a three-page report look like three reports; it is said once, at the end.
  const generatedAt = format(new Date(), 'yyyy-MM-dd HH:mm');
  const pageCount = doc.getNumberOfPages();
  // Single-patient report, so every summary carries the same MRN; the first is the document's.
  const mrn = summaries[0]?.patientId || '-';
  const footerIdentity = sanitize(
    `Monthly Care Management Billing Report  |  MRN ${mrn}  |  ${periodLabel}`,
  );

  for (let page = 1; page <= pageCount; page++) {
    const isLastPage = page === pageCount;
    doc.setPage(page);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');

    // The rule sits directly above whatever the band holds, so a page carrying only the
    // identity line does not open a hand's width of white space above it.
    doc.setDrawColor(215, 215, 215);
    doc.setLineWidth(0.3);
    const ruleY = isLastPage ? pageHeight - 22 : pageHeight - 12;
    doc.line(MARGIN, ruleY, pageWidth - MARGIN, ruleY);

    doc.setTextColor(130, 130, 130);
    if (isLastPage) {
      const note = doc.splitTextToSize(
        sanitize(
          'This report was system-generated by Phanova as an audit-trail record supporting the Care '
          + 'Management / Remote Patient Monitoring codes billed for the period above. '
          + `Generated ${generatedAt}.`,
        ),
        contentWidth,
      );
      let ny = pageHeight - 18;
      note.forEach((line: string) => {
        doc.text(line, pageWidth / 2, ny, { align: 'center' });
        ny += 3.4;
      });
    }

    // Fixed at the same height on every page, so it does not jump as a reader flips.
    doc.text(footerIdentity, MARGIN, pageHeight - 8);
    // Always, not only on a multi-page report: "Page 1 of 1" is how a filed single page
    // says it is the whole document rather than the survivor of one.
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - MARGIN, pageHeight - 8, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  }

  return doc.output('blob');
}

export default generateBillingReportPdf;
